import { useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZONES = [
  "Kandivali", "Malad (Base)", "Borivali", "Goregaon", "Jogeshwari", "Andheri",
  "Parle", "Santacruz", "Bandra", "Mahim", "Grant Road", "Dadar", "Wadala",
  "Mumbai Centre", "Marine Drive", "Churchgate", "Colaba", "Balkeshwar",
  "Byculla", "Mahalaxmi", "Mulund", "Powai", "Hiranandani", "Bhandup",
  "Vikhroli", "Sakinaka", "Chakala", "Marol", "Ghatkopar", "Chembur",
  "Govandi", "Lokhandwala", "Saat Bangla", "Dahisar", "Mira Road", "Thane",
  "Charni Road", "Chincholi Bandar",
];

export default function Locations() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const checkArea = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/service-areas/check`, { params: { q: query.trim() } });
      setResult(res.data);
    } catch {
      setResult({ served: false, message: "Could not check right now. Please call +91 9867439947 to confirm." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="locations" className="bg-[#f8fafc] text-[#0f172a] py-24 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-[#1e3a8a] block">Hyper-Local Contractor Coverage</span>
            <h2 className="display-font text-4xl sm:text-5xl uppercase tracking-tight text-[#0f172a]">
              Serving 37 Areas Across Mumbai
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Our specialized mobile teams with heavy polishing equipment cover Western Suburbs, South Mumbai, Central Suburbs, and Thane with fast same-day site visits.
            </p>

            <div className="bg-white p-6 border border-slate-200 rounded-lg shadow-sm space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-900">Pin Code &amp; Area Quick Checker</h4>
              <form onSubmit={checkArea} className="flex gap-2">
                <input
                  data-testid="area-checker-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Area or pin code (e.g. Andheri, 400097)"
                  className="w-full text-xs px-3 py-2.5 border border-slate-300 rounded focus:border-[#1e3a8a] focus:outline-none"
                />
                <button
                  data-testid="area-checker-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2.5 bg-[#1e3a8a] hover:bg-[#1e40af] text-white text-xs font-bold uppercase rounded transition-colors disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Check"}
                </button>
              </form>
              {result && (
                <p
                  data-testid="area-checker-result"
                  className={`text-[11px] font-semibold flex items-start gap-1.5 ${result.served ? "text-emerald-700" : "text-red-600"}`}
                >
                  {result.served ? (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
                  )}
                  {result.message}
                </p>
              )}
              {!result && (
                <p className="text-[11px] text-emerald-700 font-semibold">✓ Free on-site inspection available in all 37 listed zones</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white p-6 border border-slate-200 rounded-lg">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 pb-2 border-b border-slate-100">
                Active Service Zones • Anas Enterprises
              </h3>
              <div data-testid="service-zones-list" className="flex flex-wrap gap-2 text-xs font-medium text-slate-700">
                {ZONES.map((zone) => (
                  <span
                    key={zone}
                    className={`px-3 py-1.5 rounded ${
                      zone.includes("Base")
                        ? "bg-blue-50 text-[#1e3a8a] border border-blue-200 font-bold"
                        : "bg-slate-100"
                    }`}
                  >
                    {zone}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
